import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, 
  CheckCircle, 
  Clock, 
  Settings, 
  FileText, 
  BarChart3,
  User,
  Mail,
  Phone,
  Calendar
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useAuthAndProfile } from '@/hooks/useProfile';

export const ProfileDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, loading } = useAuthAndProfile();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Name fallback logic: profile data → OAuth metadata → email → 'User'
  const metadata = user.user_metadata || {};
  const fallbackFirst = metadata.given_name || metadata.full_name?.split(' ')[0] || '';
  const fallbackLast = metadata.family_name || metadata.full_name?.split(' ').slice(1).join(' ') || '';
  
  const firstName = profile?.first_name || fallbackFirst || '';
  const lastName = profile?.last_name || fallbackLast || '';
  const displayName = (firstName || lastName) ? `${firstName} ${lastName}`.trim() : (user.email || 'User');
  
  // Verification states
  const hasEmail = !!user.email;
  const emailVerified = hasEmail && !!user.email_confirmed_at;
  const hasPhone = !!user.phone;
  const phoneVerified = hasPhone && !!user.phone_confirmed_at;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div className="mb-4 md:mb-0">
            <h1 className="text-3xl font-bold text-foreground">
              Welcome back, {displayName}!
            </h1>
            <p className="text-muted-foreground">
              Manage your life insurance applications and account settings
            </p>
          </div>
          <div className="flex items-center gap-2">
            {hasEmail && (
              <Badge variant={emailVerified ? "default" : "secondary"} className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Email {emailVerified ? 'verified' : 'unverified'}
              </Badge>
            )}
            {hasPhone && (
              <Badge variant={phoneVerified ? "default" : "secondary"} className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Phone {phoneVerified ? 'verified' : 'unverified'}
              </Badge>
            )}
            <Button variant="outline" onClick={handleSignOut}>
              Sign out
            </Button>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Active Policies */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Active Policies
              </CardTitle>
              <CardDescription>
                Your current life insurance coverage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No active policies yet</h3>
                <p className="text-muted-foreground mb-4">
                  You haven't purchased any life insurance policies yet.
                </p>
                <Button onClick={() => navigate('/')}>Browse insurance options</Button>
              </div>
            </CardContent>
          </Card>

          {/* Applications in Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Applications in Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6">
                <BarChart3 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-3">
                  No applications in progress
                </p>
                <Button size="sm" onClick={() => navigate('/')}>
                  Start application
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Account & Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary" />
                Account & Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {hasEmail && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Email {emailVerified ? 'verified' : 'pending verification'}</span>
                  </div>
                  <CheckCircle className={`h-4 w-4 ${emailVerified ? 'text-green-500' : 'text-yellow-500'}`} />
                </div>
              )}
              
              {hasPhone && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Phone {phoneVerified ? 'verified' : 'pending verification'}</span>
                  </div>
                  <CheckCircle className={`h-4 w-4 ${phoneVerified ? 'text-green-500' : 'text-yellow-500'}`} />
                </div>
              )}

                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => navigate('/account')}
                >
                  Manage account
                </Button>
            </CardContent>
          </Card>

          {/* Profile Information */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Profile Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Name</Label>
                    <p className="text-sm">{displayName}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Age</Label>
                    <p className="text-sm">{profile?.age || '—'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                    <p className="text-sm">{user.email || 'No email on file'}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Phone</Label>
                    <p className="text-sm">{user.phone || 'No phone on file'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Email Verified</Label>
                    <p className="text-sm">
                      {hasEmail ? (emailVerified ? 'Yes' : 'No') : '—'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Phone Verified</Label>
                    <p className="text-sm">
                      {hasPhone ? (phoneVerified ? 'Yes' : 'No') : '—'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/profile')}
                >
                  Edit profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Helper component for labels
const Label: React.FC<{ className?: string; children: React.ReactNode }> = ({ 
  className = '', 
  children 
}) => (
  <label className={className}>{children}</label>
);